import { useState, useEffect, useRef } from 'react';
import { styled } from 'styled-components';
import { Record } from './Record';
import recordImg from '../../assets/optimized/record.webp';
import { extractYouTubeVideoId } from '../../lib/playlistService';

export type SongInfo = {
    url?: string;
    spotifyPlaylistId?: string;
    spotifyPlaylistUrl?: string;
    youtubeUrl?: string;
    youtubeVideoId?: string;
    title?: string;
    artist?: string;
};

type RecordPlayerProps = {
    labelSrc?: string;
    initialPlaying?: boolean;
    songUrl?: string;
    spotifyPlaylistId?: string;
    spotifyPlaylistUrl?: string;
    youtubeUrl?: string;
    youtubeVideoId?: string;
    songInfo?: SongInfo;
};

type PlayerState = 'stopped' | 'accelerating' | 'playing' | 'decelerating';

/**
 * RecordPlayer component that emulates a record player state machine
 * Double-click to toggle play/pause with eased acceleration/deceleration
 */
export function RecordPlayer({ 
    labelSrc, 
    initialPlaying = false,
    songUrl,
    spotifyPlaylistId,
    spotifyPlaylistUrl,
    youtubeUrl,
    youtubeVideoId,
    songInfo,
}: RecordPlayerProps) {
    const [isPlaying, setIsPlaying] = useState(initialPlaying);
    const [angleDeg, setAngleDeg] = useState(0);
    const [velocityRps, setVelocityRps] = useState(0);
    const [state, setState] = useState<PlayerState>(initialPlaying ? 'playing' : 'stopped');
    const [albumArtUrl, setAlbumArtUrl] = useState<string | undefined>(labelSrc);
    const rafRef = useRef<number | null>(null);
    const lastTimeRef = useRef<number | null>(null);
    const lastTapTimeRef = useRef<number>(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const spotifyWindowRef = useRef<Window | null>(null);
    const DOUBLE_TAP_THRESHOLD = 300; // ms

    // Determine audio URL from props
    const audioUrl = songUrl || songInfo?.url;
    const spotifyUrl = spotifyPlaylistUrl || songInfo?.spotifyPlaylistUrl;
    
    // Determine YouTube URL and video ID
    const resolvedYoutubeUrl = youtubeUrl || songInfo?.youtubeUrl;
    const resolvedYoutubeVideoId = youtubeVideoId || songInfo?.youtubeVideoId || 
        (resolvedYoutubeUrl ? extractYouTubeVideoId(resolvedYoutubeUrl) : null);

    // Debug logging for YouTube props
    useEffect(() => {
        console.log('[RecordPlayer] YouTube props:', {
            youtubeUrl,
            youtubeVideoId,
            songInfoYoutubeUrl: songInfo?.youtubeUrl,
            songInfoYoutubeVideoId: songInfo?.youtubeVideoId,
            resolvedYoutubeUrl,
            resolvedYoutubeVideoId,
            showYouTube: resolvedYoutubeUrl && resolvedYoutubeVideoId,
        });
    }, [youtubeUrl, youtubeVideoId, songInfo?.youtubeUrl, songInfo?.youtubeVideoId, resolvedYoutubeUrl, resolvedYoutubeVideoId]);

    // Target velocity: 33⅓ RPM = 0.5556 revolutions per second
    const TARGET_RPS = 0.5556;
    const ACCEL_K = 4; // Acceleration constant
    const DECEL_K = 2; // Deceleration constant

    useEffect(() => {
        if (state === 'stopped') {
            // No animation needed when stopped
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            lastTimeRef.current = null;
            return;
        }

        const animate = (currentTime: number) => {
            if (lastTimeRef.current === null) {
                lastTimeRef.current = currentTime;
                rafRef.current = requestAnimationFrame(animate);
                return;
            }

            const dt = (currentTime - lastTimeRef.current) / 1000; // Convert to seconds
            lastTimeRef.current = currentTime;

            if (state === 'playing') {
                // Constant velocity when playing
                setAngleDeg((prevAngle) => {
                    const newAngle = prevAngle + TARGET_RPS * 360 * dt;
                    return newAngle % 360;
                });
                rafRef.current = requestAnimationFrame(animate);
                return;
            }

            setVelocityRps((prevVelocity) => {
                let targetRps = 0;
                let k = DECEL_K;

                if (state === 'accelerating') {
                    targetRps = TARGET_RPS;
                    k = ACCEL_K;
                } else if (state === 'decelerating') {
                    targetRps = 0;
                    k = DECEL_K;
                }

                // Exponential easing: velocityRps += (targetRps - velocityRps) * (1 - exp(-k * dt))
                const newVelocity = prevVelocity + (targetRps - prevVelocity) * (1 - Math.exp(-k * dt));

                // Update angle based on current velocity
                setAngleDeg((prevAngle) => {
                    const newAngle = prevAngle + newVelocity * 360 * dt;
                    return newAngle % 360;
                });

                // State transitions
                if (state === 'accelerating' && Math.abs(newVelocity - TARGET_RPS) < 0.01) {
                    setState('playing');
                } else if (state === 'decelerating' && Math.abs(newVelocity) < 0.01) {
                    setState('stopped');
                    setVelocityRps(0);
                }

                return newVelocity;
            });

            rafRef.current = requestAnimationFrame(animate);
        };

        rafRef.current = requestAnimationFrame(animate);

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            lastTimeRef.current = null;
        };
    }, [state]);

    // Handle play/pause toggle
    useEffect(() => {
        if (isPlaying && state === 'stopped') {
            setState('accelerating');
        } else if (!isPlaying && state === 'playing') {
            setState('decelerating');
        }
    }, [isPlaying, state]);

    // Fetch Spotify playlist metadata for album art
    useEffect(() => {
        if (!spotifyUrl) {
            // Reset to labelSrc if no Spotify URL
            setAlbumArtUrl(labelSrc);
            return;
        }

        // Fetch playlist metadata using Spotify oEmbed API (works for public playlists)
        const oembedUrl = `https://open.spotify.com/oembed?url=${encodeURIComponent(spotifyUrl)}`;
        
        fetch(oembedUrl)
            .then((res) => {
                if (!res.ok) throw new Error('Failed to fetch playlist metadata');
                return res.json();
            })
            .then((data) => {
                // oEmbed returns thumbnail_url which is the playlist cover art
                if (data.thumbnail_url) {
                    // Replace small thumbnail with larger image if available
                    const largeImage = data.thumbnail_url.replace('300x300', '640x640');
                    setAlbumArtUrl(largeImage);
                } else {
                    // Fallback to provided labelSrc
                    setAlbumArtUrl(labelSrc);
                }
            })
            .catch((error) => {
                console.warn('Failed to fetch Spotify playlist metadata:', error);
                // Fallback to provided labelSrc on error
                setAlbumArtUrl(labelSrc);
            });
    }, [spotifyUrl, labelSrc]);

    // Sync audio playback with rotation state
    useEffect(() => {
        // YouTube playback is handled by react-player in Record component
        // No need to control it here - it syncs via isPlaying prop
        
        // Handle Spotify playlist - open in new window when playing starts (backward compatibility)
        if (spotifyUrl && !audioUrl && !resolvedYoutubeUrl) {
            if (state === 'playing') {
                // Open Spotify playlist in new window when record starts playing
                // Note: Spotify requires user interaction to play, so we open the playlist
                // The user can then play it in Spotify's interface
                if (!spotifyWindowRef.current || spotifyWindowRef.current.closed) {
                    spotifyWindowRef.current = window.open(spotifyUrl, '_blank', 'noopener,noreferrer');
                }
            }
            // Don't close window when pausing - let user control Spotify directly
            return;
        }

        // Handle direct audio file playback (when no YouTube)
        if (!audioRef.current || !audioUrl || resolvedYoutubeUrl) return;

        if (state === 'playing') {
            audioRef.current.play().catch((error) => {
                console.warn('Audio playback failed:', error);
            });
        } else if (state === 'stopped' || state === 'decelerating') {
            audioRef.current.pause();
        }
    }, [state, audioUrl, spotifyUrl, resolvedYoutubeUrl]);

    // Initialize audio element
    useEffect(() => {
        if (!audioUrl) return;

        const audio = new Audio(audioUrl);
        audio.preload = 'auto';
        audio.loop = true;
        audioRef.current = audio;

        return () => {
            audio.pause();
            audio.src = '';
            audioRef.current = null;
        };
    }, [audioUrl]);

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsPlaying((prev) => !prev);
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
        e.stopPropagation();
        const now = Date.now();
        const timeSinceLastTap = now - lastTapTimeRef.current;
        
        if (timeSinceLastTap < DOUBLE_TAP_THRESHOLD) {
            setIsPlaying((prev) => !prev);
            lastTapTimeRef.current = 0; // Reset to prevent triple-tap
        } else {
            lastTapTimeRef.current = now;
        }
    };

    // Combine song info from props
    const combinedSongInfo: SongInfo = {
        url: audioUrl,
        spotifyPlaylistId: spotifyPlaylistId || songInfo?.spotifyPlaylistId,
        spotifyPlaylistUrl: spotifyPlaylistUrl || songInfo?.spotifyPlaylistUrl,
        youtubeUrl: resolvedYoutubeUrl,
        youtubeVideoId: resolvedYoutubeVideoId || undefined,
        title: songInfo?.title,
        artist: songInfo?.artist,
    };

    // Determine if we should show YouTube player or static image
    const showYouTube = Boolean(resolvedYoutubeUrl || resolvedYoutubeVideoId);
    const showLabelImage = !showYouTube && (albumArtUrl || labelSrc);

    // Log display decision
    useEffect(() => {
        console.log('[RecordPlayer] Display decision:', {
            showYouTube,
            showLabelImage,
            hasResolvedUrl: !!resolvedYoutubeUrl,
            hasResolvedVideoId: !!resolvedYoutubeVideoId,
            hasAlbumArt: !!albumArtUrl,
            hasLabelSrc: !!labelSrc,
        });
    }, [showYouTube, showLabelImage, resolvedYoutubeUrl, resolvedYoutubeVideoId, albumArtUrl, labelSrc]);

    return (
        <Container 
            onDoubleClick={handleDoubleClick} 
            onTouchEnd={handleTouchEnd}
            data-allow-pointer-events="true"
        >
            <Record 
                recordSrc={recordImg} 
                labelSrc={showLabelImage ? (albumArtUrl || labelSrc) : undefined}
                rotationDeg={angleDeg}
                songInfo={combinedSongInfo}
                youtubeUrl={
                    showYouTube
                        ? (resolvedYoutubeUrl ||
                           (resolvedYoutubeVideoId ? `https://www.youtube.com/watch?v=${resolvedYoutubeVideoId}` : undefined))
                        : undefined
                }
                youtubeVideoId={showYouTube ? resolvedYoutubeVideoId : undefined}
                isPlaying={state === 'playing'}
            />
        </Container>
    );
}

const Container = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    background: transparent;
    cursor: pointer;
`;

